import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Home, Search, ArrowUpDown, Trash2, Sparkles } from "lucide-react";
import { getApiUrl } from "@/utils/api";

const API = getApiUrl();

const CONFIDENCE_STYLES = {
  high: "bg-green-100 text-green-700 border-green-200",
  medium: "bg-amber-100 text-amber-700 border-amber-200",
  low: "bg-red-100 text-red-700 border-red-200",
};

function formatDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
}

export default function ValuationsAdmin() {
  const [valuations, setValuations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [postcode, setPostcode] = useState("");
  const [sort, setSort] = useState("newest");
  const [deletingId, setDeletingId] = useState(null);
  const [deduping, setDeduping] = useState(false);

  const load = async (pc = postcode, s = sort) => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API}/admin/valuations`, {
        params: { postcode: pc, sort: s },
        withCredentials: true,
      });
      setValuations(data.valuations || []);
    } catch {
      toast.error("Failed to load valuations");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearch = (e) => {
    e.preventDefault();
    load(postcode, sort);
  };

  const toggleSort = () => {
    const next = sort === "newest" ? "agent" : "newest";
    setSort(next);
    load(postcode, next);
  };

  const handleDelete = async (valuationId) => {
    if (!window.confirm("Delete this valuation? This cannot be undone.")) return;
    setDeletingId(valuationId);
    try {
      await axios.delete(`${API}/admin/valuations/${valuationId}`, { withCredentials: true });
      setValuations((prev) => prev.filter((v) => v.valuation_id !== valuationId));
      toast.success("Valuation deleted");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete valuation");
    } finally {
      setDeletingId(null);
    }
  };

  const handleDedupe = async () => {
    if (!window.confirm("Remove duplicate valuations (same house number/name + postcode), keeping only the newest of each?")) return;
    setDeduping(true);
    try {
      const { data } = await axios.post(`${API}/admin/valuations/dedupe`, {}, { withCredentials: true });
      toast.success(data.removed > 0 ? `Removed ${data.removed} duplicate valuation(s)` : "No duplicates found");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to remove duplicates");
    } finally {
      setDeduping(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="valuations-admin">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Home className="h-6 w-6" /> Valuations
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Every AI-generated price estimate, grounded in HM Land Registry sold comparables.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <form onSubmit={handleSearch} className="flex items-center gap-2">
            <div className="relative">
              <Search className="h-3.5 w-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                value={postcode}
                onChange={(e) => setPostcode(e.target.value)}
                placeholder="Search postcode…"
                className="text-sm border border-input rounded px-2 py-1.5 pl-7 bg-background w-44"
                data-testid="valuations-postcode-search"
              />
            </div>
            <button
              type="submit"
              className="text-sm px-3 py-1.5 border border-input rounded hover:bg-muted/40"
              data-testid="valuations-search-btn"
            >
              Search
            </button>
          </form>
          <button
            type="button"
            onClick={toggleSort}
            className="text-sm px-3 py-1.5 border border-input rounded hover:bg-muted/40 flex items-center gap-1.5 whitespace-nowrap"
            data-testid="valuations-sort-toggle"
            title="Toggle sort order"
          >
            <ArrowUpDown className="h-3.5 w-3.5" />
            {sort === "newest" ? "Newest first" : "By agent"}
          </button>
          <button
            type="button"
            onClick={handleDedupe}
            disabled={deduping}
            className="text-sm px-3 py-1.5 border border-input rounded hover:bg-muted/40 flex items-center gap-1.5 whitespace-nowrap disabled:opacity-60"
            data-testid="valuations-dedupe-btn"
            title="Remove duplicate valuations (same house number/name + postcode), keeping the newest"
          >
            {deduping ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
            Remove duplicates
          </button>
        </div>
      </div>

      <section className="border border-border rounded bg-background overflow-x-auto">
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : valuations.length === 0 ? (
          <div className="p-8 text-center text-sm text-muted-foreground" data-testid="valuations-empty">
            No valuations generated yet.
          </div>
        ) : (
          <table className="w-full text-sm min-w-[820px]">
            <thead className="text-left text-muted-foreground text-xs bg-muted/40">
              <tr>
                <th className="p-3">Property</th>
                <th className="p-3">Postcode</th>
                <th className="p-3">Agent</th>
                <th className="p-3">Estimated range</th>
                <th className="p-3">Confidence</th>
                <th className="p-3">Date</th>
                <th className="p-3 text-right">Manage</th>
              </tr>
            </thead>
            <tbody>
              {valuations.map((v) => (
                <tr
                  key={v.valuation_id}
                  className="border-t border-border hover:bg-muted/20"
                  data-testid={`valuation-row-${v.valuation_id}`}
                >
                  <td className="p-3 max-w-[220px]">
                    {v.property_id ? (
                      <Link
                        to={`/property/${v.property_id}`}
                        className="text-primary hover:underline line-clamp-2"
                        data-testid={`valuation-property-link-${v.valuation_id}`}
                      >
                        {v.property_address || v.property_id}
                      </Link>
                    ) : (
                      <span className="text-muted-foreground">{v.property_address || "—"}</span>
                    )}
                  </td>
                  <td className="p-3 font-mono text-xs">{v.postcode || "—"}</td>
                  <td className="p-3 font-medium">{v.agent_name || "Unknown"}</td>
                  <td className="p-3 whitespace-nowrap">
                    {v.estimated_low && v.estimated_high
                      ? `£${Number(v.estimated_low).toLocaleString()} – £${Number(v.estimated_high).toLocaleString()}`
                      : "—"}
                  </td>
                  <td className="p-3">
                    <span
                      className={`text-xs px-2 py-1 rounded border capitalize ${CONFIDENCE_STYLES[v.confidence] || "bg-muted text-foreground border-border"}`}
                      data-testid={`valuation-confidence-${v.valuation_id}`}
                    >
                      {v.confidence || "low"}
                    </span>
                  </td>
                  <td className="p-3 whitespace-nowrap text-xs">{formatDate(v.generated_at)}</td>
                  <td className="p-3 text-right">
                    <button
                      type="button"
                      onClick={() => handleDelete(v.valuation_id)}
                      disabled={deletingId === v.valuation_id}
                      className="inline-flex items-center gap-1 text-xs font-medium text-red-600 hover:underline disabled:opacity-60"
                      data-testid={`valuation-delete-${v.valuation_id}`}
                      title="Delete valuation"
                    >
                      {deletingId === v.valuation_id ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        <Trash2 className="h-3 w-3" />
                      )}
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}
